import React from 'react';
import { useNavigate } from 'react-router-dom';

interface HeaderProps {
  title: string;
  subtitle?: string;
  children?: React.ReactNode;
  onLogout?: () => void;
  onSettingsClick?: () => void;
  showSettingsButton?: boolean;
}

const Header: React.FC<HeaderProps> = ({
  title,
  subtitle,
  children,
  onLogout,
  onSettingsClick,
  showSettingsButton = true
}) => {
  const navigate = useNavigate();

  const handleSettingsClick = () => {
    if (title === 'Admin Dashboard' || title === 'Admin Settings') {
      navigate('/admin/settings');
    } else if (title === 'Tenant Dashboard' || title === 'Tenant Settings') {
      navigate('/settings');
    }
  };

  return (
    <div className="w-full bg-gradient-to-br from-main-medium via-main-dark to-main-medium px-8 py-6 flex items-center justify-between flex-wrap gap-4 shadow-2xl border-b border-white/5 backdrop-blur-sm">
      <div className="flex items-center gap-4">
        <div className="flex items-center justify-center w-12 h-12 bg-gradient-to-br from-purple-600 to-purple-700 rounded-xl shadow-lg">
          <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
          </svg>
        </div>
        <div>
          <h1 className="text-3xl font-bold text-white mb-0.5 tracking-tight bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
            {title}
          </h1>
          {subtitle && <p className="text-gray-400 text-sm font-medium">{subtitle}</p>}
        </div>
      </div>
      <div className="flex items-center gap-3">
        {showSettingsButton && (title === 'Admin Dashboard' || title === 'Admin Settings' || title === 'Tenant Dashboard' || title === 'Tenant Settings') && (
          <button
            onClick={handleSettingsClick}
            className="group px-4 py-2 bg-gradient-to-r from-blue-600/80 to-blue-700/80 backdrop-blur-sm text-white font-semibold rounded-lg hover:from-blue-600 hover:to-blue-700 transition-all duration-300 flex items-center gap-2.5 shadow-lg hover:shadow-xl hover:shadow-blue-500/25 hover:scale-105 active:scale-95 border border-blue-500/20"
            title="Settings"
          >
            <svg
              className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
              />
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            <span>Settings</span>
          </button>
        )}
        {onLogout && (
          <button
            onClick={onLogout}
            className="group px-4 py-2 bg-gradient-to-r from-red-600/90 to-red-700/90 backdrop-blur-sm text-white font-semibold rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-300 flex items-center gap-2.5 shadow-lg hover:shadow-xl hover:shadow-red-500/25 hover:scale-105 active:scale-95 border border-red-500/20"
          >
            <svg
              className="w-5 h-5 group-hover:translate-x-0.5 transition-transform duration-300"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
            <span>Logout</span>
          </button>
        )}
      </div>
    </div>
  );
};

export default Header;
